import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import { EmployeePermission } from 'src/api/permissions';
import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { RoutePath, TabItem } from 'src/routes/types';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { getUserState } from 'src/store/user/selectors';

export interface TabsContextValue {
  tabItems: TabItem[];
  selectedTabIndex: number | undefined;
  selectTabIndex: (index: number) => void;
}

const tabItemsByOrder: TabItem[] = [
  {
    label: 'Администрирование',
    path: RoutePath.Administrator,
    permission: EmployeePermission.EditPlans,
  },
  {
    label: 'Пространства',
    path: RoutePath.Spaces,
    permission: EmployeePermission.EditPlans,
  },
  {
    label: 'Рабочие места',
    path: RoutePath.DeskBooking,
    permission: EmployeePermission.BookDesks,
  },
  {
    label: 'Комнаты',
    path: RoutePath.RoomBooking,
    permission: EmployeePermission.BookRooms,
  },
  {
    label: 'Парковочные места',
    path: RoutePath.Parking,
    permission: EmployeePermission.BookParkingPlaces,
  },
  {
    label: 'Мои бронирования',
    path: RoutePath.MyBookings,
    permission: EmployeePermission.ViewBookedObjects,
  },
  {
    label: 'Поиск коллеги',
    path: RoutePath.Search,
    permission: EmployeePermission.ViewBookedObjects,
  },
  // {
  //   label: 'Обратная связь',
  //   path: RoutePath.Feedback,
  //   permission: EmployeePermission.ViewFeedbacks,
  // },
];

export const TabsContext = createContext<TabsContextValue | undefined>(
  undefined,
);
TabsContext.displayName = 'TabsContext';

interface TabsProviderProps {
  children?: ReactNode;
}

export const TabsProvider: FC<TabsProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { data } = useSelector(getUserState);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const tabItems = useMemo((): TabItem[] => {
    const tabs: TabItem[] = [];
    if (!data) return tabs;

    for (const tab of tabItemsByOrder) {
      if (data.permissions.has(tab.permission)) tabs.push(tab);
    }

    return tabs;
  }, [data]);

  const selectedTabIndex = useMemo(() => {
    if (!pathname) return;

    for (let i = 0; i < tabItems.length; i++) {
      const tab = tabItems[i];
      if (matchPath(tab.path, pathname)) return i;
    }
  }, [pathname, tabItems]);

  const selectTabIndex = useCallback(
    (index: number) => {
      if (selectedTabIndex === index) return;

      dispatch(sidesheetActions.close());
      navigate(tabItems[index].path);
    },
    [dispatch, navigate, selectedTabIndex, tabItems],
  );

  useEffect(() => {
    if (tabItems.length && !selectedTabIndex) {
      selectTabIndex(0);
    }
  }, [selectTabIndex, selectedTabIndex, tabItems.length]);

  const contextValue = useMemo(
    (): TabsContextValue => ({
      tabItems,
      selectedTabIndex,
      selectTabIndex,
    }),
    [selectTabIndex, selectedTabIndex, tabItems],
  );

  return (
    <TabsContext.Provider value={contextValue}>{children}</TabsContext.Provider>
  );
};

export const useTabs = (): TabsContextValue => useRequiredContext(TabsContext);
